import classNames from 'classnames';
import { ComponentType, PropsWithChildren, UIEvent } from 'react';
import { Checkbox, Table } from 'rsuite';
import { TableColumnProps } from 'rsuite/lib/Table/TableColumn';
import { useKeepTableScrollPosition } from '../../hooks/useKeepTableScrollPosition';
import { useNetworkPricesTableSize } from '../../hooks/useNetworkPricesTableSize';
import { useNetworkPricesStore } from '../../NetworkPrices.store';
import { NetworkPriceLine, NetworkPriceRow } from '../../types';
import { CLUSTER_COLUMN_WIDTH, findLineCluster, getClusterColumns, getLineKey, getScrollViewportWidth } from '../../utils';
import { AveragePriceCell } from '../AveragePriceCell';
import {
  ClusterCompetitorPriceCell,
  ClusterCpiCell,
  ClusterMarginCell,
  ClusterPmzCell,
  ClusterRetailPriceCell,
  UnavailableClusterDetailCell,
} from '../ClusterDetailCells';
import { ClusterHeaderCell } from '../ClusterHeaderCell';
import { ClusterPriceCell, UnavailableClusterPriceCell } from '../ClusterPriceCell';
import { NetworkProductCell } from '../NetworkProductCell';
import styles from './NetworkPricesTable.module.scss';

type NetworkPricesTableProps = { lines: NetworkPriceLine[]; isLoading: boolean };
const Column = Table.Column as ComponentType<PropsWithChildren<TableColumnProps>>;
const { HeaderCell, Cell } = Table;
const syncCheckboxChecked =
  (checked: boolean) =>
  (input: HTMLInputElement | null): void => {
    if (input) input.checked = checked;
  };
// rsuite scrolls the table with transforms; focusing an off-screen header (Tab) natively scrolls the overflow-hidden root
// and misaligns fixed columns. Undo that native scroll.
const resetNativeTableScroll = ({ target }: UIEvent<HTMLDivElement>): void => {
  if (target instanceof HTMLElement && target.classList.contains('rs-table') && target.scrollLeft !== 0) target.scrollLeft = 0;
};
const DETAIL_COLUMNS = [
  { key: 'cpi', label: 'CPI', width: 80, Component: ClusterCpiCell },
  { key: 'margin', label: 'Margem', width: 80, Component: ClusterMarginCell },
  { key: 'pmz', label: 'PMZ', width: 100, Component: ClusterPmzCell },
  { key: 'retailPrice', label: 'Preço vigente', width: 100, Component: ClusterRetailPriceCell },
  { key: 'competitorPrice', label: 'Preço concorrente', width: 100, Component: ClusterCompetitorPriceCell },
];

const DETAIL_COLUMNS_WIDTH = DETAIL_COLUMNS.reduce((total, { width }) => total + width, 0);

export const NetworkPricesTable = ({ lines, isLoading }: NetworkPricesTableProps) => {
  const { containerRef, width, height } = useNetworkPricesTableSize();
  const pageKey = useNetworkPricesStore(({ pagination }) => `${pagination.page}:${pagination.size}`);
  const expandedClusterId = useNetworkPricesStore(state => state.expandedClusterId);
  const toggleExpandedCluster = useNetworkPricesStore(state => state.toggleExpandedCluster);
  const selectedLineKeys = useNetworkPricesStore(state => state.selectedLineKeys);
  const toggleLineSelection = useNetworkPricesStore(state => state.toggleLineSelection);
  const setSelectedLineKeys = useNetworkPricesStore(state => state.setSelectedLineKeys);
  const clusterColumns = getClusterColumns(lines);
  const expandedIndex = clusterColumns.findIndex(column => column.clusterId === expandedClusterId);
  const scrollContentWidth = clusterColumns.length * CLUSTER_COLUMN_WIDTH + (expandedIndex >= 0 ? DETAIL_COLUMNS_WIDTH : 0);
  const expandedStart = expandedIndex * CLUSTER_COLUMN_WIDTH;
  const revealTarget =
    expandedIndex >= 0 && width
      ? {
          key: String(expandedClusterId),
          range: { start: expandedStart, end: expandedStart + CLUSTER_COLUMN_WIDTH + DETAIL_COLUMNS_WIDTH },
          viewportWidth: getScrollViewportWidth(width, scrollContentWidth),
        }
      : null;
  const { handleScroll, handleDataUpdated } = useKeepTableScrollPosition(width, pageKey, revealTarget);
  const rows: NetworkPriceRow[] = lines.map(line => ({ ...line, lineKey: getLineKey(line) }));
  const pageKeys = rows.map(line => line.lineKey);
  const selectedCount = pageKeys.filter(key => selectedLineKeys.includes(key)).length;
  const isAllSelected = pageKeys.length > 0 && selectedCount === pageKeys.length;
  const isIndeterminate = selectedCount > 0 && !isAllSelected;

  return (
    <div ref={containerRef} className={styles.container} onScrollCapture={resetNativeTableScroll}>
      <Table
        key={width}
        onScroll={handleScroll}
        onDataUpdated={handleDataUpdated}
        width={width}
        height={height}
        data={rows}
        rowKey="lineKey"
        rowHeight={72}
        headerHeight={56}
        loading={isLoading}
        className={styles.table}
      >
        <Column fixed="left" width={44}>
          <HeaderCell className={styles.checkbox}>
            <Checkbox
              aria-label="Selecionar página"
              aria-checked={isIndeterminate ? 'mixed' : isAllSelected}
              checked={isAllSelected}
              indeterminate={isIndeterminate}
              inputRef={syncCheckboxChecked(isAllSelected)}
              onChange={(_value, checked) => setSelectedLineKeys(checked ? pageKeys : [])}
            />
          </HeaderCell>
          <Cell className={styles.checkbox}>
            {({ description, lineKey }: NetworkPriceRow) => {
              const isSelected = selectedLineKeys.includes(lineKey);
              return (
                <Checkbox
                  aria-label={`Selecionar ${description}`}
                  checked={isSelected}
                  inputRef={syncCheckboxChecked(isSelected)}
                  onChange={() => toggleLineSelection(lineKey)}
                />
              );
            }}
          </Cell>
        </Column>
        <Column fixed="left" flexGrow={1} minWidth={260}>
          <HeaderCell className={styles.header}>Produto</HeaderCell>
          <Cell>{(line: NetworkPriceLine) => <NetworkProductCell line={line} />}</Cell>
        </Column>
        {clusterColumns.flatMap(column => {
          const isExpanded = expandedClusterId === column.clusterId;
          const priceColumn = (
            <Column key={column.clusterId} width={CLUSTER_COLUMN_WIDTH}>
              <HeaderCell>
                <ClusterHeaderCell column={column} isExpanded={isExpanded} onToggle={() => toggleExpandedCluster(column.clusterId)} />
              </HeaderCell>
              <Cell>
                {(line: NetworkPriceLine) => {
                  const cluster = findLineCluster(line, column.clusterId);
                  return cluster ? <ClusterPriceCell line={line} cluster={cluster} /> : <UnavailableClusterPriceCell />;
                }}
              </Cell>
            </Column>
          );
          if (!isExpanded) return [priceColumn];

          return [
            priceColumn,
            ...DETAIL_COLUMNS.map(({ key, label, width, Component }) => (
              <Column key={`${column.clusterId}:${key}`} width={width}>
                <HeaderCell className={styles.header}>{label}</HeaderCell>
                <Cell>
                  {(line: NetworkPriceLine) => {
                    const cluster = findLineCluster(line, column.clusterId);
                    return cluster ? <Component cluster={cluster} /> : <UnavailableClusterDetailCell />;
                  }}
                </Cell>
              </Column>
            )),
          ];
        })}
        <Column fixed="right" width={150}>
          <HeaderCell className={classNames(styles.header, styles.average)}>Preço médio</HeaderCell>
          <Cell className={styles.average}>{(line: NetworkPriceLine) => <AveragePriceCell line={line} />}</Cell>
        </Column>
      </Table>
    </div>
  );
};
